import { useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { createEvent } from '../../hooks/useEvents';
import { getUnavailableMembers } from '../../hooks/useUnavailability';
import type { EventType, EventVisibility, MemberUnavailability, BandMember } from '../../types/database';
import UnavailabilityConflictModal from './UnavailabilityConflictModal';

interface Props {
  bandId: string;
  userId: string;
  unavailability?: MemberUnavailability[];
  members?: BandMember[];
  initialDate?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateEventModal({ bandId, userId, unavailability = [], members = [], initialDate, onClose, onSuccess }: Props) {
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState<EventType>('gig');
  const [visibility, setVisibility] = useState<EventVisibility>('public');
  const [startDate, setStartDate] = useState(initialDate ?? '');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [description, setDescription] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [ticketPrice, setTicketPrice] = useState('');
  const [notesPrivate, setNotesPrivate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflictModal, setConflictModal] = useState<MemberUnavailability[] | null>(null);

  async function doCreate() {
    setLoading(true);
    setError('');

    const startTimeISO = new Date(`${startDate}T${startTime}`).toISOString();
    const endTimeISO = endDate && endTime ? new Date(`${endDate}T${endTime}`).toISOString() : undefined;

    const { error } = await createEvent({
      band_id: bandId,
      title,
      description: description || undefined,
      event_type: eventType,
      visibility,
      start_time: startTimeISO,
      end_time: endTimeISO,
      venue_name: venueName || undefined,
      venue_address: venueAddress || undefined,
      ticket_url: ticketUrl || undefined,
      ticket_price: ticketPrice || undefined,
      notes_private: notesPrivate || undefined,
      created_by: userId,
    });

    if (error) {
      setError(error);
      setLoading(false);
    } else {
      onSuccess();
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // Check for unavailability conflicts
    if (startDate && unavailability.length > 0) {
      const conflicts = getUnavailableMembers(unavailability, startDate);
      if (conflicts.length > 0) {
        setConflictModal(conflicts);
        return;
      }
    }

    doCreate();
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
            <h2 className="text-lg font-semibold">Add Event</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Friday Night Live at The Venue"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value as EventType)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="gig">Gig</option>
                  <option value="meeting">Meeting</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Visibility</label>
                <select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as EventVisibility)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="public">Public - visible to everyone</option>
                  <option value="private">Private - band members only</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Start Time *</label>
                <input
                  type="time"
                  required
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Venue Name</label>
              <input
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="e.g. The Dog House Pub"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Venue Address</label>
              <input
                type="text"
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
                placeholder="e.g. 123 High Street, St Peter Port"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Details about the event for the public..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ticket URL</label>
                <input
                  type="url"
                  value={ticketUrl}
                  onChange={(e) => setTicketUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ticket Price</label>
                <input
                  type="text"
                  value={ticketPrice}
                  onChange={(e) => setTicketPrice(e.target.value)}
                  placeholder="e.g. Free, $10, 5 GBP"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Private Notes (band only)</label>
              <textarea
                value={notesPrivate}
                onChange={(e) => setNotesPrivate(e.target.value)}
                rows={2}
                placeholder="Internal notes only visible to band members..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {conflictModal && (
        <UnavailabilityConflictModal
          conflicts={conflictModal}
          members={members}
          eventDate={startDate}
          onCancel={() => setConflictModal(null)}
          onProceed={() => {
            setConflictModal(null);
            doCreate();
          }}
        />
      )}
    </>
  );
}
